import React, { useRef, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

const FieldsForm = ({ state, headerNames, errors, updateField }) => {
  const initialFieldTypes = useRef({});
  const [isSelected, setIsSelected] = useState(false); // Track if a selection was made
  const [fieldValue, setFieldValue] = useState(state?.exp_assigned_episodes); // Track the field value

  useEffect(() => {
    if (state) {
      Object.keys(state).forEach((fieldKey) => {
        if (fieldKey !== "id" && !(fieldKey in initialFieldTypes.current)) {
          if (fieldKey === "eb_payment_terms") {
            initialFieldTypes.current[fieldKey] = "number"; // Set type as 'number' for eb_payment_terms
          } else {
            initialFieldTypes.current[fieldKey] =
              typeof state[fieldKey] === "number" ? "number" : "text";
          }
        }
      });
      // Ensure that `exp_assigned_episodes` is set properly if it's part of state
      if (state.exp_assigned_episodes) {
        setFieldValue(state.exp_assigned_episodes);
      }
    }
  }, [state]);

  if (!state) {
    return <div>Loading...</div>;
  }

  const handleSelectChange = (fieldKey, event) => {
    const updatedValue = event.target.value;
    setFieldValue(updatedValue); // Update the selected value
    updateField(fieldKey, updatedValue); // Update parent state with the new value
    setIsSelected(true); // Mark the field as selected
  };

  const handleClickTextField = () => {
    setIsSelected(false); // Allow the field to be edited again when clicked
  };

  return (
    <Box p={2} display="flex" justifyContent="center">
      <Grid container spacing={2}>
        {Object.keys(state).map((fieldKey) => {
          if (fieldKey === "id") {
            return null; // Skip rendering the 'id' field in the form
          }

          const label = headerNames[fieldKey] || fieldKey;
          const value = state[fieldKey] || "";
          const fieldType = initialFieldTypes.current[fieldKey] || "text";

          // Handle locked_episodes field with multi-select
          if (fieldKey === "locked_episodes" && state.hasOwnProperty("locked_episodes")) {
            const lockedEpisodes = Array.isArray(state.locked_episodes) 
              ? [...state.locked_episodes].sort((a, b) => a - b) 
              : [];
            const availableEpisodes = state.no_of_episodes 
              ? Array.from({ length: state.no_of_episodes }, (_, i) => i + 1).sort((a, b) => a - b)
              : [];

            return (
              <Grid item xs={12} sm={12} key={fieldKey}>
                <FormControl fullWidth size="small">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    multiple
                    value={lockedEpisodes}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sortedValue = typeof value === 'string' 
                        ? value.split(',').map(Number).sort((a, b) => a - b)
                        : [...value].sort((a, b) => a - b);
                      updateField(fieldKey, sortedValue);
                    }}
                    input={<OutlinedInput label={label} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.sort((a, b) => a - b).map((value) => (
                          <Chip key={value} label={`Επεισόδιο ${value}`} size="small" />
                        ))}
                      </Box>
                    )}
                    error={!!errors?.[fieldKey]}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          overflowY: 'auto',
                        },
                      },
                    }}
                  >
                    {availableEpisodes.map((episode) => (
                      <MenuItem key={episode} value={episode}>
                        <Checkbox checked={lockedEpisodes.indexOf(episode) > -1} />
                        <ListItemText primary={`Επεισόδιο ${episode}`} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors?.[fieldKey] && (
                    <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                      {errors[fieldKey]}
                    </div>
                  )}
                </FormControl>
              </Grid>
            );
          }

          // Only render the Select dropdown for exp_assigned_episodes if it's available in state
          if (
            fieldKey === "exp_assigned_episodes" &&
            state.hasOwnProperty("exp_assigned_episodes")
          ) {
            if (!isSelected) {
              return (
                <Grid item xs={12} sm={12} key={fieldKey}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{label}</InputLabel>
                    <Select
                      value={fieldValue}
                      onChange={(e) => handleSelectChange(fieldKey, e)}
                      label={label}
                      error={!!errors?.[fieldKey]}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="option1">Option 1</MenuItem>
                      <MenuItem value="option2">Option 2</MenuItem>
                      <MenuItem value="option3">Option 3</MenuItem>
                    </Select>
                    {errors?.[fieldKey] && (
                      <div style={{ color: "red", fontSize: "12px" }}>
                        {errors[fieldKey]}
                      </div>
                    )}
                  </FormControl>
                </Grid>
              );
            } else {
              return (
                <Grid item xs={12} sm={12} key={fieldKey}>
                  <TextField
                    id={`${fieldKey}-id`}
                    label={label}
                    size="small"
                    fullWidth
                    value={fieldValue}
                    onClick={handleClickTextField} // Allow re-editing when clicked
                    InputProps={{ readOnly: true }} // Make it read-only
                  />
                </Grid>
              );
            }
          }

          const handleChange = (e) => {
            let updatedValue = e.target.value;

            if (fieldKey === "eb_payment_terms" || fieldKey === "p_aa" || fieldKey === "episodes_per_recording") {
              // Parse the input and set to `null` if the value is `NaN`
              updatedValue = updatedValue ? parseFloat(updatedValue) : null;
              if (isNaN(updatedValue)) {
                updatedValue = null; // Convert `NaN` explicitly to `null`
              }
            } else if (fieldType === "number") {
              // Handle other number fields
              updatedValue = updatedValue ? parseFloat(updatedValue) : "";
            }

            updateField(fieldKey, updatedValue);
          };

          return (
            <Grid item xs={12} sm={12} key={fieldKey}>
              <TextField
                id={`${fieldKey}-id`}
                label={label}
                size="small"
                fullWidth
                type={fieldType}
                value={value}
                onChange={handleChange}
                helperText={errors?.[fieldKey] || ""}
                error={!!errors?.[fieldKey]}
                InputProps={
                  fieldType === "number" ? { inputMode: "numeric" } : {}
                }
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default FieldsForm;
